/**
 * O aviso de versão nova — a metade que faltava do `registerType: 'prompt'`.
 *
 * **Sem isto, o aplicativo nunca atualizava.** O `prompt` foi escolhido de
 * propósito: trocar a versão por baixo de quem está no meio de um treino
 * recarregaria a página e perderia o que ainda não foi sincronizado. Só que
 * "prompt" pressupõe que alguém pergunte — e ninguém perguntava. O service
 * worker novo instalava, ficava esperando, e o navegador seguia servindo o
 * pacote antigo até a pessoa fechar todas as abas do aplicativo.
 *
 * O efeito prático era um deploy que não chegava: rota nova publicada no
 * servidor, e a pessoa abrindo um link dela caindo na regra de "caminho
 * desconhecido" do pacote velho.
 *
 * Fica no topo porque a base da tela é da barra de navegação, e porque um
 * aviso que exige ação não deve dividir espaço com os botões principais.
 */

import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';

export function UpdatePrompt() {
  const { t } = useTranslation(['common']);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 top-0 z-50 flex items-center gap-3 bg-(--color-sand-900) px-4 py-3 text-(--color-sand-0)"
      style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
    >
      <p className="flex-1 text-sm">{t('common:update.available')}</p>
      <button
        type="button"
        // `true` recarrega assim que o service worker novo assume. É o que a
        // pessoa pediu ao tocar aqui — sem isso, o aviso sumiria e nada mudaria.
        onClick={() => void updateServiceWorker(true)}
        className="rounded-full bg-(--color-amber-500) px-4 py-2 text-sm font-semibold text-(--color-sand-900)"
      >
        {t('common:update.reload')}
      </button>
      {/* Sem `aria-label`: ele substituiria o texto visível como nome
          acessível, e quem usa comando de voz diria "Agora não" para um botão
          que se chama outra coisa. O texto na tela já é o nome. */}
      <button type="button" onClick={() => setNeedRefresh(false)} className="text-sm underline">
        {t('common:update.later')}
      </button>
    </div>
  );
}
